// src/utils.tsx
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { FileType, FileData } from '../types/training';
import {
  FaFile, FaFolder, FaFileAlt, FaFileImage, FaFileArchive, FaQuestionCircle
} from 'react-icons/fa'; // Using react-icons

export const generateId = (): string => uuidv4();

export const getFileType = (fileNameOrMime: string): FileType => {
  const lowerName = fileNameOrMime.toLowerCase();
  if (lowerName.startsWith('image/')) return 'image';
  if (lowerName.endsWith('.zip') || lowerName.includes('zip')) return 'zip';
  if (lowerName.endsWith('.txt') || lowerName.startsWith('text/')) return 'text';
  if (lowerName.includes('.')) return 'file'; // Generic file if it has an extension
  // Basic checks - you might need more robust MIME type checking
  return 'unknown';
};

export const getIconForFileType = (type: FileType, size: number = 20): React.ReactNode => {
  switch (type) {
    case 'file': return React.createElement(FaFile as React.ComponentType<any>, { size });
    case 'folder': return React.createElement(FaFolder as React.ComponentType<any>, { size });
    case 'text': return React.createElement(FaFileAlt as React.ComponentType<any>, { size });
    case 'image': return React.createElement(FaFileImage as React.ComponentType<any>, { size });
    case 'zip': return React.createElement(FaFileArchive as React.ComponentType<any>, { size });
    default: return React.createElement(FaQuestionCircle as React.ComponentType<any>, { size });
  }
};

// Basic function to handle dropped items (can be expanded)
export const processDroppedItem = (item: DataTransferItem): Promise<FileData | null> => {
  return new Promise(async (resolve) => {
    if (item.kind === 'string' && item.type.match('^text/plain')) {
      item.getAsString((text) => {
        resolve({
          id: generateId(),
          name: `Pasted Text Snippet (${new Date().toLocaleTimeString()})`,
          type: 'text',
          textContent: text,
          size: text.length,
        });
      });
    } else if (item.kind === 'file') {
      const file = item.getAsFile();
      if (file) {
          // Basic Folder Detection (heuristic - might not always be reliable)
          // Real folder handling requires webkitGetAsEntry which is more complex
          const isLikelyFolder = !file.type && file.size % 4096 === 0 && file.name.indexOf('.') === -1;
          const fileType = isLikelyFolder ? 'folder' : getFileType(file.type || file.name);

          // For images, also convert to data URL and store in textContent
          if (fileType === 'image') {
            const reader = new FileReader();
            reader.onload = () => {
              resolve({
                id: generateId(),
                name: file.name,
                type: fileType,
                nativeFile: file,
                textContent: reader.result as string, // Store data URL
                size: file.size,
              });
            };
            reader.onerror = () => {
              console.error('Error reading image file:', file.name);
              resolve({
                id: generateId(),
                name: file.name,
                type: fileType,
                nativeFile: file,
                size: file.size,
              });
            };
            reader.readAsDataURL(file);
          } else {
            resolve({
              id: generateId(),
              name: file.name,
              type: fileType,
              nativeFile: file,
              size: file.size,
            });
          }
      } else {
          resolve(null); // Could not get file
      }
    } else {
      resolve(null); // Unsupported type
    }
  });
};

